import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronRight,
  Gavel,
  Loader2,
  Info,
  RefreshCcw,
  FileCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import JudgmentWarningBanner from "@/components/judgment/JudgmentWarningBanner";
import JudgmentDocument from "@/components/judgment/JudgmentDocument";
import { getProject } from "@/lib/profiles";
import { getLatestAnalysis } from "@/lib/analyses";
import { runJudgmentSimulation } from "@/lib/judgment";
import { listLegalTexts } from "@/lib/legal";

export default function WizardJudgment() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [legalTextsCount, setLegalTextsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  // Double-confirmation state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmStep, setConfirmStep] = useState(1); // 1 then 2
  const [ackBox, setAckBox] = useState(false);
  const [forceMode, setForceMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, an, texts] = await Promise.all([
        getProject(projectId),
        getLatestAnalysis(projectId),
        listLegalTexts(),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setAnalysis(an);
      setLegalTextsCount((texts || []).length);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger la simulation."
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = analysis?.results?.summary?.currency || "UM";
  const legalAnalysis = analysis?.results?.legal_analysis || null;
  const judgment = analysis?.results?.judgment || null;

  const canSimulate = useMemo(
    () => !!analysis && !!legalAnalysis && legalTextsCount >= 1 && !running,
    [analysis, legalAnalysis, legalTextsCount, running]
  );

  const openConfirm = (force) => {
    setForceMode(!!force);
    setAckBox(false);
    setConfirmStep(1);
    setConfirmOpen(true);
  };

  const proceedConfirm = async () => {
    if (confirmStep === 1) {
      setConfirmStep(2);
      return;
    }
    // Step 2 → actually run
    setConfirmOpen(false);
    await doSimulate(forceMode);
  };

  const doSimulate = async (force) => {
    if (!analysis) return;
    setRunning(true);
    try {
      const res = await runJudgmentSimulation(analysis.id, { force });
      const fresh = await getLatestAnalysis(projectId);
      setAnalysis(fresh);
      toast.success(
        res.cached
          ? "Jugement chargé depuis le cache."
          : "Jugement vitaliste généré avec succès."
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "La simulation de jugement a échoué."
      );
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="wizard-judgment-loading"
      >
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wizard-judgment-page">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Nouveau projet · Étape 6/7
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Simulation de jugement vitaliste
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {project?.name}
          {project?.fiscal_year && ` · Exercice ${project.fiscal_year}`} —
          Rédaction d'un jugement FICTIF au nom du Tribunal Vitaliste, à partir
          des données budgétaires et de l'analyse juridique déjà validée.
        </p>
      </div>

      <WizardStepper current={6} />
      <TutorialDeepLink
        chapter={7}
        section="section-7-1"
        id="wizard-step-6-judgment"
        label="Avertissement légal et lecture du jugement simulé"
        duration={8}
      />

      {/* Mandatory reinforced warning */}
      <JudgmentWarningBanner />

      {/* Preconditions guard */}
      {!analysis && (
        <Card className="border-dashed" data-testid="no-vitalist-card">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Analyse vitaliste requise</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Lancez d'abord les étapes 1 à 3 pour générer l'analyse vitaliste.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/confirm`)
              }
              data-testid="go-confirm-btn"
            >
              Aller à l'étape 3
            </Button>
          </CardContent>
        </Card>
      )}

      {analysis && !legalAnalysis && (
        <Card className="border-dashed" data-testid="no-legal-card">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Analyse juridique requise</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              La simulation de jugement repose sur l'analyse juridique du
              Cycle 5. Veuillez la générer avant de continuer.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/legal`)
              }
              data-testid="go-legal-btn"
            >
              Aller à l'étape 5 (juridique)
            </Button>
          </CardContent>
        </Card>
      )}

      {analysis && legalAnalysis && legalTextsCount === 0 && (
        <Card className="border-dashed" data-testid="no-corpus-card">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">
              Corpus juridique indisponible
            </h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Au moins un texte légal doit rester dans votre corpus pour que
              la juridiction fictive puisse citer des articles.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/legal`)
              }
              data-testid="back-legal-btn"
            >
              Retour au corpus
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trigger card */}
      {analysis && legalAnalysis && legalTextsCount >= 1 && (
        <Card
          className="border-[#1A3C5E]/30 bg-[#1A3C5E]/5 dark:border-brand-gold/30 dark:bg-brand-gold/5"
          data-testid="simulate-trigger-card"
        >
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Gavel className="mt-0.5 h-5 w-5 text-[#1A3C5E] dark:text-brand-gold" />
              <div className="text-sm">
                <div className="font-semibold">
                  {judgment
                    ? "Jugement déjà prononcé — vous pouvez le régénérer."
                    : "Prêt à simuler le jugement vitaliste."}
                </div>
                <div className="text-xs text-muted-foreground">
                  1 seul appel GPT-4o sera effectué.{" "}
                  {legalAnalysis.infractions?.length || 0} infraction(s) sur
                  lesquelles le tribunal fictif se prononcera.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {judgment && (
                <Button
                  variant="outline"
                  onClick={() => openConfirm(true)}
                  disabled={!canSimulate}
                  data-testid="rerun-judgment-btn"
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Régénérer
                </Button>
              )}
              <Button
                onClick={() => openConfirm(false)}
                disabled={!canSimulate}
                data-testid="simulate-judgment-btn"
                className="bg-[#1A3C5E] text-white hover:bg-[#1A3C5E]/90 dark:bg-brand-gold dark:text-[#1A3C5E] dark:hover:bg-brand-gold/90"
              >
                {running ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Délibération en cours…
                  </>
                ) : (
                  <>
                    <Gavel className="mr-2 h-4 w-4" />
                    Simuler le Jugement Vitaliste
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {judgment ? (
        <JudgmentDocument judgment={judgment} currency={currency} />
      ) : (
        analysis &&
        legalAnalysis &&
        legalTextsCount >= 1 && (
          <Card className="border-dashed" data-testid="judgment-empty">
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              <FileCheck2 className="h-10 w-10 text-muted-foreground" />
              <h3 className="text-lg font-semibold">
                Aucun jugement simulé pour l'instant
              </h3>
              <p className="max-w-md text-sm text-muted-foreground">
                Cliquez sur « Simuler le Jugement Vitaliste » pour faire
                délibérer la juridiction fictive.
              </p>
            </CardContent>
          </Card>
        )
      )}

      {/* Footer nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() => navigate(`/app/projects/${projectId}/wizard/legal`)}
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Étape précédente
        </Button>
        <Button
          variant="default"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/report`)
          }
          data-testid="wizard-next-report-btn"
        >
          Générer le rapport
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Double confirmation modal */}
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
            setAckBox(false);
            setConfirmStep(1);
          }
        }}
      >
        <AlertDialogContent
          className="sm:max-w-lg"
          data-testid="confirm-judgment-modal"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
              <Gavel className="h-5 w-5" />
              {confirmStep === 1
                ? "Confirmation — 1/2"
                : "Confirmation finale — 2/2"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {confirmStep === 1 ? (
                  <>
                    <p>
                      Vous êtes sur le point de faire rédiger un{" "}
                      <strong>jugement FICTIF</strong> par le Tribunal
                      Vitaliste. Cette simulation :
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        est un <strong>document strictement pédagogique</strong>,
                      </li>
                      <li>
                        n'a <strong>aucune valeur juridictionnelle</strong>,
                      </li>
                      <li>
                        s'appuie sur votre analyse juridique et la théorie
                        vitaliste (ELY Mustapha).
                      </li>
                    </ul>
                    <p className="italic text-muted-foreground">
                      Cliquez sur « Continuer » pour atteindre la confirmation
                      finale.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Confirmez explicitement que vous comprenez la portée
                      <strong> pédagogique uniquement</strong> de cette
                      simulation avant de lancer l'appel au modèle.
                    </p>
                    <label
                      htmlFor="ack-pedagogical"
                      className="flex cursor-pointer items-start gap-2 rounded-md border border-rose-300 bg-rose-50/50 p-3 dark:bg-rose-950/30"
                    >
                      <Checkbox
                        id="ack-pedagogical"
                        checked={ackBox}
                        onCheckedChange={(v) => setAckBox(!!v)}
                        data-testid="ack-pedagogical-check"
                        className="mt-0.5"
                      />
                      <span>
                        Je reconnais que ce jugement est{" "}
                        <strong>fictif et pédagogique</strong>, qu'il ne
                        constitue ni une décision de justice, ni un avis
                        juridique, et que j'assume la responsabilité de son
                        usage.
                      </span>
                    </label>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-judgment-btn">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent default auto-close on step 1 — we want to go to step 2
                if (confirmStep === 1) {
                  e.preventDefault();
                }
                proceedConfirm();
              }}
              disabled={confirmStep === 2 && !ackBox}
              data-testid={
                confirmStep === 1
                  ? "continue-judgment-btn"
                  : "confirm-final-judgment-btn"
              }
              className="bg-[#C0392B] hover:bg-[#C0392B]/90"
            >
              {confirmStep === 1
                ? "Continuer"
                : "Lancer la simulation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
